import { supabase } from '../client';
import { MenuCategory, MenuItem, BASE_VARIANT_LABEL_ID } from '@deskbay/shared/types';
import { logger } from '@deskbay/shared';
import { normalizeDisplayPrice, priceToCents } from '../../../utils/price';
import type { DbCategoryRow, DbMenuItemRow } from './types';

/**
 * Remove orphan variantLabels from an item's translations: keys that don't correspond to any
 * current variant id nor the reserved '__base__'. These accumulate when a user recreates/deletes
 * variants (new UUIDs) leaving stale translated labels behind. Pruned only when the item HAS
 * variants (otherwise we can't know the valid id set and must not touch existing data).
 * Returns a new translations object; never mutates the input.
 */
function pruneOrphanVariantLabels(
    translations: MenuItem['translations'] | undefined,
    variants: MenuItem['variants'] | undefined
): MenuItem['translations'] {
    if (!translations) return {};
    const hasVariants = Array.isArray(variants) && variants.length > 0;
    if (!hasVariants) return translations; // no known id set → leave untouched
    const validIds = new Set<string>(variants!.map(v => v.id).filter(Boolean));
    validIds.add(BASE_VARIANT_LABEL_ID);
    const out: NonNullable<MenuItem['translations']> = {};
    for (const [lang, t] of Object.entries(translations)) {
        if (!t || typeof t !== 'object') { out[lang] = t; continue; }
        if (!t.variantLabels || typeof t.variantLabels !== 'object') { out[lang] = t; continue; }
        const cleaned: Record<string, string> = {};
        for (const [vk, vv] of Object.entries(t.variantLabels)) {
            if (validIds.has(vk)) cleaned[vk] = vv;
        }
        out[lang] = Object.keys(cleaned).length > 0
            ? { ...t, variantLabels: cleaned }
            : (() => { const { variantLabels: _drop, ...rest } = t; return rest; })();
    }
    return out;
}

// normalizeDisplayPrice and priceToCents are imported from @deskbay/shared/utils/price
// (single source of truth, with correct thousands/decimal handling — see that file).

/**
 * Crea un backup dello stato corrente del menu.
 * Il menu viene letto server-side dalla RPC (non serve fetch client-side).
 */
export async function createMenuBackup(
    restaurantId: string | number,
    backupType: 'auto_save' | 'manual_admin' | 'pre_translate' | 'pre_restore' | 'ai_rescan',
    notes?: string
  ): Promise<string | null> {
    try {
      const { data: backupId, error } = await supabase.rpc('create_menu_backup', {
        p_restaurant_id: String(restaurantId),
        p_backup_type: backupType,
        p_metadata: notes ? { notes } : {}
      });

      if (error) {
        logger.api.error('Failed to call create_menu_backup RPC', error, 'database.createMenuBackup');
        return null;
      }
      return backupId;
    } catch (error: unknown) {
      logger.api.error('Error creating menu backup', error, 'database.createMenuBackup');
      return null;
    }
}

/**
 * Sincronizza l'intero menù (Categorie + Piatti).
 *
 * STRATEGIA BATCH UPSERT (refactor 2026-05-15):
 * Da N+M chiamate HTTP sequenziali a 4 round paralleli/batch.
 *
 * Round 1: SELECT categorie + items esistenti (per identificare orphans)
 * Round 2: UPSERT batch di tutte le categorie in una sola chiamata
 * Round 3: UPSERT batch di tutti gli items in una sola chiamata
 *          (FK rispettata: le categorie esistono già nel DB dal Round 2)
 * Round 4: DELETE orphans (categories + items in parallelo)
 *
 * KEY DESIGN:
 * - I payload INCLUDONO `id` (UUID client-side) → upsert su PK match
 * - Client UUID == DB UUID per sempre (no drift)
 * - `click_count` e `created_at` NON nel payload → preservati
 * - Safety guard "menu scomparso" invariata
 * - Backup automatico pre-save invariato (fire-and-forget)
 *
 * Vedi `.docs/plans/SAVEFULLMENU_REFACTORING_PLAN.md` per dettagli completi.
 */
export async function saveFullMenu(restaurantId: string | number, categories: MenuCategory[]) {
    try {
      logger.api.info(`Starting BATCH save for ${categories.length} categories`, 'database.saveFullMenu');

      // === BACKUP AUTOMATICO PRE-SAVE (invariato, fire-and-forget) ===
      if (categories.length > 0) {
        createMenuBackup(restaurantId, 'auto_save', `Auto backup pre-save for ${categories.length} categories`).then(id => {
          if (id) logger.api.debug(`Auto backup created: ${id}`, 'database.saveFullMenu');
        });
      }

      // === ROUND 1a: SELECT categorie esistenti ===
      const { data: existingCats, error: fetchCatsError } = await supabase
        .from('categories')
        .select('id')
        .eq('restaurant_id', restaurantId);

      if (fetchCatsError) {
        logger.api.error('Error fetching existing categories', fetchCatsError, 'database.saveFullMenu');
        throw fetchCatsError;
      }

      const existingCatIds = new Set<string>((existingCats || []).map(c => c.id as string));

      // === SAFETY GUARD: empty save intenzionale ===
      // Se l'utente salva con 0 categorie e il DB ne ha → elimina tutto (CASCADE elimina anche items).
      if (categories.length === 0 && existingCatIds.size > 0) {
        logger.api.info(
          `Saving empty menu — deleting all ${existingCatIds.size} existing categories from DB.`,
          'database.saveFullMenu'
        );
        const { error: deleteCatsError } = await supabase
          .from('categories')
          .delete()
          .eq('restaurant_id', restaurantId);
        if (deleteCatsError) {
          logger.api.error('Error deleting all categories', deleteCatsError, 'database.saveFullMenu');
          throw deleteCatsError;
        }
        logger.api.info('Menu cleared successfully', 'database.saveFullMenu');
        return;
      }

      // === ROUND 1b: SELECT items esistenti ===
      // Eseguita dopo categories perché serve `existingCatIds`. Non parallelizzabile senza JOIN.
      let existingItemIds = new Set<string>();
      if (existingCatIds.size > 0) {
        const { data: existingItems, error: fetchItemsError } = await supabase
          .from('menu_items')
          .select('id, category_id')
          .in('category_id', Array.from(existingCatIds));

        if (fetchItemsError) {
          logger.api.error('Error fetching existing items', fetchItemsError, 'database.saveFullMenu');
          throw fetchItemsError;
        }

        existingItemIds = new Set<string>((existingItems || []).map(i => i.id as string));
      }

      // === ROUND 2: Batch upsert categorie ===
      // Payload include `id` (UUID client). PostgREST traduce in INSERT ... ON CONFLICT (id) DO UPDATE.
      // Dopo questa chiamata, TUTTI i category.id sono garantiti esistere nel DB (FK ok per Round 3).
      const categoryPayloads = categories.map((cat, catIdx) => ({
        id: String(cat.id),
        restaurant_id: restaurantId,
        name: cat.categoryName,
        description: cat.description || '',
        image_url: cat.imageUrl || '',
        sort_order: catIdx,
        translations: cat.translations || {}
      }));

      const finalCatIds = new Set<string>(categoryPayloads.map(c => c.id));

      if (categoryPayloads.length > 0) {
        const { error: catUpsertError } = await supabase
          .from('categories')
          .upsert(categoryPayloads, { onConflict: 'id' });

        if (catUpsertError) {
          logger.api.error('Category batch upsert error', catUpsertError, 'database.saveFullMenu');
          throw catUpsertError;
        }
        logger.api.debug(`Upserted ${categoryPayloads.length} categories`, 'database.saveFullMenu');
      }

      // === ROUND 3: Batch upsert items ===
      // Costruisce il payload di TUTTI gli items, in ordine di categoria/posizione.
      // `category_id` punta a `cat.id` (UUID client = UUID DB grazie a Round 2).
      const itemPayloads: Array<Record<string, unknown>> = [];
      const finalItemIds = new Set<string>();

      categories.forEach((cat) => {
        if (!cat.items || cat.items.length === 0) return;
        cat.items.forEach((item, itemIdx) => {
          const normalizedItemPrice = normalizeDisplayPrice(item.price);
          // SINGLE SOURCE OF TRUTH: `price` (string) is what the user edits; price_cents is ALWAYS
          // derived from it. Never reuse a possibly-stale item.priceCents — doing so let the public
          // menu (which reads price_cents) show an old price after an edit. See plan §8.1.
          const computedPriceCents = priceToCents(normalizedItemPrice);

          const enrichedVariants = (item.variants && item.variants.length > 0)
            ? item.variants.map(v => {
                const normalizedVariantPrice = normalizeDisplayPrice(v.price);
                return {
                  ...v,
                  price: normalizedVariantPrice,
                  // Same rule for variants: always derive from the variant's price string.
                  price_cents: priceToCents(normalizedVariantPrice)
                };
              })
            : null;

          itemPayloads.push({
            id: String(item.id),
            category_id: String(cat.id),
            name: item.name,
            description: item.description || '',
            price: normalizedItemPrice,
            price_cents: computedPriceCents,
            variants: enrichedVariants,
            image_url: item.imageUrl || '',
            is_active: item.isActive ?? true,
            allergen_ids: item.allergenIds || [],
            tag_ids: item.tagIds || [],
            sort_order: itemIdx,
            translations: pruneOrphanVariantLabels(item.translations, item.variants),
            temporary_reopen_at: item.temporary_reopen_at || null,
            hide_base_price: item.hideBasePrice ?? false,
            base_price_label: item.basePriceLabel || null
          });
          finalItemIds.add(String(item.id));
        });
      });

      if (itemPayloads.length > 0) {
        const { error: itemUpsertError } = await supabase
          .from('menu_items')
          .upsert(itemPayloads, { onConflict: 'id' });

        if (itemUpsertError) {
          logger.api.error('Item batch upsert error', itemUpsertError, 'database.saveFullMenu');
          logger.api.error(`Item upsert error details: code=${(itemUpsertError as {code?:string}).code} message=${(itemUpsertError as {message?:string}).message} details=${(itemUpsertError as {details?:string}).details} hint=${(itemUpsertError as {hint?:string}).hint}`, itemUpsertError, 'database.saveFullMenu');
          // Log first offending payload field to help diagnose type issues
          if (itemPayloads.length > 0) {
            const sample = itemPayloads[0];
            logger.api.error('First item payload sample', { id: sample.id, translations_type: typeof sample.translations, variants_type: typeof sample.variants, hide_base_price: sample.hide_base_price, base_price_label: sample.base_price_label }, 'database.saveFullMenu');
          }
          throw itemUpsertError;
        }
        logger.api.debug(`Upserted ${itemPayloads.length} items`, 'database.saveFullMenu');
      }

      // === ROUND 4: Cleanup orphans (parallelo) ===
      // Items orfani: erano in existingItems ma non più nel payload (rimossi o spostati fuori).
      // Categorie orfane: erano in existingCats ma non più nel payload (rimosse). CASCADE elimina i loro items.
      const itemsToDelete = Array.from(existingItemIds).filter(id => !finalItemIds.has(id));
      const catsToDelete = Array.from(existingCatIds).filter(id => !finalCatIds.has(id));

      const cleanupPromises: PromiseLike<unknown>[] = [];

      if (itemsToDelete.length > 0) {
        logger.api.info(`Deleting ${itemsToDelete.length} removed items`, 'database.saveFullMenu');
        cleanupPromises.push(
          supabase.from('menu_items').delete().in('id', itemsToDelete).then(({ error }) => {
            if (error) logger.api.error('Error deleting orphan items', error, 'database.saveFullMenu');
            // Non rilanciamo: cleanup non blocca il save principale (comportamento invariato dal legacy).
          })
        );
      }

      if (catsToDelete.length > 0) {
        logger.api.info(`Deleting ${catsToDelete.length} removed categories`, 'database.saveFullMenu');
        cleanupPromises.push(
          supabase.from('categories').delete().in('id', catsToDelete).then(({ error }) => {
            if (error) logger.api.error('Error deleting orphan categories', error, 'database.saveFullMenu');
          })
        );
      }

      if (cleanupPromises.length > 0) {
        await Promise.all(cleanupPromises);
      }

      logger.api.info(
        `Menu BATCH save completed: ${finalCatIds.size} categories, ${finalItemIds.size} items`,
        'database.saveFullMenu'
      );
    } catch (error) {
      logger.api.error('ERROR in saveFullMenu', error, 'database.saveFullMenu');
      throw error;
    }
}

/**
 * [PERFORMANCE] Lazy load menu data separately
 * Call this only when user opens Menu tab
 */
export async function getRestaurantMenu(restaurantId: string) {
    const startTime = performance.now();

    const { data: categories, error } = await supabase
      .from('categories')
      .select('*, items:menu_items(*)')
      .eq('restaurant_id', restaurantId)
      .order('sort_order', { ascending: true });

    if (error) {
      logger.api.error('getRestaurantMenu Error', error, 'database.getRestaurantMenu');
      return [];
    }

    const duration = performance.now() - startTime;
    logger.performance.info(`getRestaurantMenu: ${duration.toFixed(0)}ms`, 'database.getRestaurantMenu');

    return (categories || []).map((cat: DbCategoryRow) => ({
      id: cat.id,
      categoryName: cat.name,
      description: cat.description || '',
      imageUrl: cat.image_url || '',
      translations: (cat.translations || {}) as MenuCategory['translations'],
      items: (cat.items || []).sort((a: DbMenuItemRow, b: DbMenuItemRow) => (a.sort_order || 0) - (b.sort_order || 0)).map((item: DbMenuItemRow) => ({
        id: item.id,
        name: item.name,
        description: item.description || '',
        price: item.price || '',
        priceCents: item.price_cents ?? null,
        variants: (item.variants || undefined) as MenuItem['variants'],
        imageUrl: item.image_url || '',
        isActive: item.is_active ?? true,
        temporary_reopen_at: item.temporary_reopen_at || null,
        hideBasePrice: item.hide_base_price ?? false,
        basePriceLabel: item.base_price_label || undefined,
        allergenIds: item.allergen_ids || [],
        tagIds: item.tag_ids || [],
        translations: (item.translations || {}) as MenuItem['translations']
      }))
    }));
}
